import type { Type } from '@angular/core';
import {
  BlockDiagnostic,
  BlockEnvironment,
  BlockManifest,
  BlockReference,
  BlockRegistration,
  BlockSettings,
  BlockSettingsSchema,
  ContractReference,
  TWO_COLUMN_WORKSPACE,
  WorkspaceBlock,
} from './block-contract';

const identifier = /^[a-z][a-z0-9]*(?:[.-][a-z0-9]+)*$/;
const contexts = new Set([
  'personId',
  'hearingId',
  'proceedingId',
  'sittingId',
  'workflowInstanceId',
  'stepOccurrenceId',
]);
const hosts = new Set(['step-shell', 'case-context', 'dashboard-expansion']);
const regions = new Set(['main', 'supporting']);
const key = (r: BlockReference): string =>
  `${r.provider}/${r.code}@${r.version}`;
const own = (o: object, p: string): boolean =>
  Object.prototype.hasOwnProperty.call(o, p);
function requireContract(r: ContractReference): void {
  if (
    !identifier.test(r.code) ||
    !Number.isSafeInteger(r.version) ||
    r.version < 1
  ) {
    throw new Error(
      'Contracts require a stable code and a positive integer version.',
    );
  }
}
function freeze<T>(value: T): T {
  if (value && typeof value === 'object') {
    Object.values(value).forEach(freeze);
    Object.freeze(value);
  }
  return value;
}
function validateManifest(m: BlockManifest): void {
  requireContract(m);
  requireContract(m.settingsSchema);
  if (
    !identifier.test(m.provider) ||
    !m.name.trim() ||
    !m.category.trim() ||
    !m.description.trim()
  ) {
    throw new Error(
      'Block provider, name, category and description are required.',
    );
  }
  if (m.deprecated !== undefined && !m.deprecated.trim())
    throw new Error('Explain the deprecation.');
  if (m.keywords.some((k) => !k.trim()))
    throw new Error('Search keywords must not be empty.');
  if (
    new Set(m.contextNeeds).size !== m.contextNeeds.length ||
    m.contextNeeds.some((c) => !contexts.has(c))
  ) {
    throw new Error('Context needs must be unique known keys.');
  }
  const reads = new Set<string>();
  for (const read of m.reads) {
    requireContract(read);
    requireContract(read.dataContract);
    if (read.parameterContract) requireContract(read.parameterContract);
    if (
      read.requestParameters &&
      (new Set(read.requestParameters).size !== read.requestParameters.length ||
        read.requestParameters.some(
          (name) =>
            !/^[a-zA-Z][a-zA-Z0-9]*$/.test(name) ||
            ['constructor', 'prototype'].includes(name) ||
            own(read.parameters, name),
        ))
    ) {
      throw new Error(
        'Request parameters must be unique names separate from trusted context bindings.',
      );
    }
    const readKey = `${read.code}@${read.version}`;
    if (reads.has(readKey)) throw new Error('Duplicate named read.');
    reads.add(readKey);
    if (
      Object.entries(read.parameters).some(
        ([p, c]) => !p.trim() || !m.contextNeeds.includes(c),
      )
    ) {
      throw new Error('Read parameters must map to declared context needs.');
    }
  }
  const placements = m.placements.map((p) => `${p.host}/${p.region}`);
  if (
    !placements.length ||
    new Set(placements).size !== placements.length ||
    m.placements.some((p) => !hosts.has(p.host) || !regions.has(p.region))
  ) {
    throw new Error(
      'Declare unique supported placements; shell-owned regions are reserved.',
    );
  }
  const layout = m.layout;
  if (
    !layout.widths.includes('full') ||
    layout.widths.some((w) => w !== 'full' && w !== 'half') ||
    new Set(layout.widths).size !== layout.widths.length ||
    layout.responsive !== 'stack-in-order' ||
    !Number.isFinite(layout.minimumWidthPx) ||
    layout.minimumWidthPx <= 0 ||
    !Number.isFinite(layout.minimumHeightPx) ||
    layout.minimumHeightPx < 0
  ) {
    throw new Error(
      'Declare full-width fallback, ordered stacking and valid minimum sizes.',
    );
  }
  if (!['single-per-workspace', 'repeatable'].includes(m.instances))
    throw new Error('Declare instance policy.');
  for (const [name, field] of Object.entries(m.settingsSchema.fields)) {
    if (
      !/^[a-zA-Z][a-zA-Z0-9]*$/.test(name) ||
      ['constructor', 'prototype'].includes(name) ||
      typeof field.required !== 'boolean'
    )
      throw new Error('Invalid settings field.');
    switch (field.type) {
      case 'string':
        if (!Number.isSafeInteger(field.maxLength) || field.maxLength < 1)
          throw new Error('Invalid string limit.');
        break;
      case 'integer':
        if (
          !Number.isSafeInteger(field.minimum) ||
          !Number.isSafeInteger(field.maximum) ||
          field.minimum > field.maximum
        )
          throw new Error('Invalid integer bounds.');
        break;
      case 'enum':
        if (
          !field.values.length ||
          field.values.some((v) => typeof v !== 'string') ||
          new Set(field.values).size !== field.values.length
        )
          throw new Error('Invalid enum values.');
        break;
      case 'boolean':
        break;
      default:
        throw new Error('Unsupported settings field type.');
    }
  }
}

/** Validates the deliberately bounded scalar settings schema, without coercion. */
export function parseBlockSettings<const S extends BlockSettingsSchema>(
  schema: S,
  input: unknown,
): BlockSettings<S> {
  if (
    !input ||
    typeof input !== 'object' ||
    Array.isArray(input) ||
    ![Object.prototype, null].includes(Object.getPrototypeOf(input))
  ) {
    throw new Error('Settings must be a plain object.');
  }
  const values = input as Record<string, unknown>;
  for (const name of Object.keys(values)) {
    if (!own(schema.fields, name)) throw new Error(`Unknown setting: ${name}.`);
  }
  const result: Record<string, unknown> = {};
  for (const [name, field] of Object.entries(schema.fields)) {
    if (!own(values, name)) {
      if (field.required) throw new Error(`Required setting: ${name}.`);
      continue;
    }
    const value = values[name];
    const valid =
      field.type === 'boolean'
        ? typeof value === 'boolean'
        : field.type === 'string'
          ? typeof value === 'string' && value.length <= field.maxLength
          : field.type === 'integer'
            ? typeof value === 'number' &&
              Number.isSafeInteger(value) &&
              value >= field.minimum &&
              value <= field.maximum
            : field.type === 'enum' &&
              typeof value === 'string' &&
              field.values.includes(value);
    if (!valid) throw new Error(`Invalid setting: ${name} (${field.type}).`);
    Object.defineProperty(result, name, { value, enumerable: true });
  }
  return Object.freeze(result) as BlockSettings<S>;
}

/** Code-installed registry. Manifests are metadata; neither imports nor data reads occur during catalog lookup. */
export class BlockRegistry {
  private readonly registrations = new Map<string, BlockRegistration>();
  private readonly loads = new Map<string, Promise<Type<unknown>>>();

  register(registration: BlockRegistration): void {
    validateManifest(registration.manifest);
    if (typeof registration.load !== 'function')
      throw new Error('A code-installed loader is required.');
    const id = key(registration.manifest);
    if (this.registrations.has(id)) throw new Error(`Duplicate block: ${id}.`);
    // Own an immutable snapshot so callers cannot change the validated contract afterwards.
    this.registrations.set(id, {
      manifest: freeze(structuredClone(registration.manifest)),
      load: registration.load,
    });
  }

  catalog(search = ''): readonly BlockManifest[] {
    const terms = search.toLowerCase().trim().split(/\s+/).filter(Boolean);
    return [...this.registrations.values()]
      .map((r) => r.manifest)
      .filter((m) => {
        const text = [
          m.provider,
          m.code,
          m.name,
          m.category,
          m.description,
          ...m.keywords,
        ]
          .join(' ')
          .toLowerCase();
        return terms.every((t) => text.includes(t));
      })
      .sort((a, b) => key(a).localeCompare(key(b)));
  }

  resolve(reference: BlockReference): BlockManifest {
    const registration = this.registrations.get(key(reference));
    if (!registration)
      throw new Error(
        `Unavailable block: ${key(reference)}. No version fallback is permitted.`,
      );
    return registration.manifest;
  }

  /** Complete-workspace validation makes singleton and duplicate instance-ID checks meaningful. */
  validate(
    blocks: readonly WorkspaceBlock[],
    environment: BlockEnvironment,
  ): readonly BlockDiagnostic[] {
    const diagnostics: BlockDiagnostic[] = [];
    const ids = new Set<string>();
    for (const block of blocks) {
      const add = (code: string, message: string): void => {
        diagnostics.push({ instanceId: block.instanceId, code, message });
      };
      if (!block.instanceId.trim() || ids.has(block.instanceId))
        add('instance-id', 'Each block needs a unique nonempty instance ID.');
      ids.add(block.instanceId);
      let manifest: BlockManifest;
      try {
        manifest = this.resolve(block.block);
      } catch (error) {
        add('unavailable', (error as Error).message);
        continue;
      }
      if (
        manifest.instances === 'single-per-workspace' &&
        blocks.filter(
          (b) =>
            b.block.provider === manifest.provider &&
            b.block.code === manifest.code,
        ).length > 1
      ) {
        add(
          'singleton',
          `${manifest.name} permits one instance per workspace, across versions.`,
        );
      }
      if (
        environment.layoutProfile.code !== TWO_COLUMN_WORKSPACE.code ||
        environment.layoutProfile.version !== TWO_COLUMN_WORKSPACE.version
      ) {
        add('layout-profile', 'Unsupported workspace layout profile.');
      }
      if (
        block.placement.host !== environment.host ||
        !manifest.placements.some(
          (p) =>
            p.host === block.placement.host &&
            p.region === block.placement.region,
        )
      ) {
        add(
          'placement',
          `${manifest.name} is not supported in ${block.placement.host}/${block.placement.region}.`,
        );
      }
      if (!manifest.layout.widths.includes(block.width))
        add('width', `${manifest.name} does not support ${block.width} width.`);
      const available = environment.regionWidths[block.placement.region];
      if (
        available === undefined ||
        !Number.isFinite(available) ||
        available < manifest.layout.minimumWidthPx
      ) {
        add(
          'minimum-size',
          `${manifest.name} needs at least ${manifest.layout.minimumWidthPx}px of region width.`,
        );
      }
      for (const context of manifest.contextNeeds) {
        if (!environment.contextKeys.includes(context))
          add('context', `${manifest.name} requires ${context}.`);
      }
      if (block.settingsVersion !== manifest.settingsSchema.version)
        add(
          'settings-version',
          'The exact registered settings version is required.',
        );
      try {
        parseBlockSettings(manifest.settingsSchema, block.settings);
      } catch (error) {
        add('settings', (error as Error).message);
      }
    }
    return diagnostics;
  }

  /** Host uses returned spans in original order; a narrow region stacks all half-width blocks. */
  layout(
    blocks: readonly WorkspaceBlock[],
    environment: BlockEnvironment,
  ): readonly {
    instanceId: string;
    columns: 6 | 12;
    minimumHeightPx: number;
  }[] {
    this.assertCompatible(blocks, environment);
    return blocks.map((block) => {
      const m = this.resolve(block.block);
      const width = environment.regionWidths[block.placement.region]!;
      const regionBlocks = blocks.filter(
        (b) =>
          b.placement.region === block.placement.region && b.width === 'half' && b.tabId === block.tabId,
      );
      const minimumHalf = Math.max(
        0,
        ...regionBlocks.map((b) => this.resolve(b.block).layout.minimumWidthPx),
      );
      const fitsHalf =
        width >=
        Math.max(
          TWO_COLUMN_WORKSPACE.halfRowMinimumPx,
          minimumHalf * 2 + TWO_COLUMN_WORKSPACE.gapPx,
        );
      return {
        instanceId: block.instanceId,
        columns: block.width === 'half' && fitsHalf ? 6 : 12,
        minimumHeightPx: m.layout.minimumHeightPx,
      };
    });
  }

  async load(
    instanceId: string,
    blocks: readonly WorkspaceBlock[],
    environment: BlockEnvironment,
  ): Promise<Type<unknown>> {
    this.assertCompatible(blocks, environment);
    const block = blocks.find((b) => b.instanceId === instanceId);
    if (!block) throw new Error(`Unknown block instance: ${instanceId}.`);
    const id = key(block.block);
    let pending = this.loads.get(id);
    if (!pending) {
      pending = Promise.resolve()
        .then(() => this.registrations.get(id)!.load())
        .then((component) => {
          if (typeof component !== 'function')
            throw new Error(`Loader returned no component: ${id}.`);
          return component;
        })
        .catch((error) => {
          this.loads.delete(id);
          throw error;
        });
      this.loads.set(id, pending);
    }
    return pending;
  }

  private assertCompatible(
    blocks: readonly WorkspaceBlock[],
    environment: BlockEnvironment,
  ): void {
    const diagnostics = this.validate(blocks, environment);
    if (diagnostics.length)
      throw new Error(
        diagnostics.map((d) => `${d.instanceId}: ${d.message}`).join('\n'),
      );
  }
}
